import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { baseURL } from "../../constants/endPoints";
import * as apiService from "../../services/v2/apiService";
import ActiveBuyerReportView from "./ActiveBuyerReportView";
import { adaptApiResponse } from "./adaptApiResponse";
import "./ActiveBuyerReport.scss";

const trimBase = (url) => (url || "").trim().replace(/\/+$/, "");

const buildEndpoint = (idOrToken, shared) =>
  shared
    ? `${trimBase(baseURL)}/api/v2/active-buyer-report/shared/${encodeURIComponent(idOrToken)}/data`
    : `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(idOrToken)}/data`;

export const isPdfResponse = (contentType) =>
  String(contentType || "").toLowerCase().includes("pdf");

export const extractPdfFilename = (disposition, fallback) => {
  const match = String(disposition || "").match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
};

export const downloadReportPdf = async (inventoryId) => {
  const url = `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(inventoryId)}`;
  const response = await apiService.apiCallBlob(url, "GET");
  const contentType = response.headers?.["content-type"] || "";
  if (!isPdfResponse(contentType)) {
    const err = new Error("PDF generation unavailable");
    err.status = response.status;
    throw err;
  }
  const disposition = response.headers?.["content-disposition"] || "";
  const filename = extractPdfFilename(
    disposition,
    `active-buyer-report-${inventoryId}.pdf`
  );
  const objectUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const downloadSharedReportPdf = async (token) => {
  const url = `${trimBase(baseURL)}/api/v2/active-buyer-report/shared/${encodeURIComponent(token)}/pdf`;
  // Public tokenized share view: no bearer token, matches fetchReportData's
  // shared branch below.
  const response = await fetch(url, {
    headers: { "api-key": process.env.REACT_APP_REST_API_KEY || "" },
  });
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok || !isPdfResponse(contentType)) {
    const err = new Error("PDF generation unavailable");
    err.status = response.status;
    throw err;
  }
  const disposition = response.headers.get("content-disposition") || "";
  const filename = extractPdfFilename(disposition, `active-buyer-report-${token}.pdf`);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const fetchReportData = async (inventoryId, { signal, shared = false } = {}) => {
  const url = buildEndpoint(inventoryId, shared);

  if (!shared) {
    // Authed fetches go through the app api client so the access token
    // auto-refreshes; the raw-fetch path below stays for the public
    // tokenized share view, which needs no auth.
    try {
      const body = await apiService.apiCall(
        url,
        "GET",
        undefined,
        null,
        null,
        null,
        false,
        signal
      );
      const result =
        body && Object.prototype.hasOwnProperty.call(body, "result")
          ? body.result
          : body;
      if (!result) {
        const err = new Error("Empty report payload");
        err.status = 500;
        throw err;
      }
      return result;
    } catch (e) {
      if (e?.name === "AbortError" || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        throw e;
      }
      if (e?.status) throw e;
      const status = e?.response?.status || 0;
      if (status === 0 && process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[ActiveBuyerReport] network error, falling back to fixture:", e);
        const fixture = await import("./__fixtures__/sampleData.json");
        return fixture.default || fixture;
      }
      const apiMessage = e?.response?.data?.message || "";
      const err = new Error(
        status === 404
          ? "Property not found"
          : apiMessage
            ? `Failed to load report (${status}): ${apiMessage}`
            : status
              ? `Failed to load report (${status})`
              : "Network error"
      );
      err.status = status;
      throw err;
    }
  }

  // Public tokenized share view: never attach the viewer's bearer token,
  // so a logged-in agent opening a /r/:token link still gets the redacted
  // public payload, not an authenticated one.
  const headers = {
    "Content-Type": "application/json",
    "api-key": process.env.REACT_APP_REST_API_KEY || ""
  };

  let response;
  try {
    response = await fetch(url, { headers, signal });
  } catch (networkErr) {
    if (networkErr?.name === "AbortError") throw networkErr;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[ActiveBuyerReport] network error, falling back to fixture:", networkErr);
      const fixture = await import("./__fixtures__/sampleData.json");
      return fixture.default || fixture;
    }
    const err = new Error("Network error");
    err.status = 0;
    throw err;
  }

  if (response.status === 404) {
    const err = new Error("Property not found");
    err.status = 404;
    throw err;
  }
  if (!response.ok) {
    let apiMessage = "";
    try {
      const body = await response.json();
      apiMessage = body?.message || "";
    } catch (_e) {
      // body wasn't JSON
    }
    const err = new Error(
      apiMessage
        ? `Failed to load report (${response.status}): ${apiMessage}`
        : `Failed to load report (${response.status})`
    );
    err.status = response.status;
    throw err;
  }

  const body = await response.json();
  // API wraps payload as { message, errorCode, result }
  const result = body && Object.prototype.hasOwnProperty.call(body, "result") ? body.result : body;
  if (!result) {
    const err = new Error("Empty report payload");
    err.status = 500;
    throw err;
  }
  return result;
};

const LoadingSkeleton = () => (
  <div className="abr-page-loading" role="status" aria-live="polite">
    <div className="abr-page-loading-inner">
      <div className="abr-skel-bar abr-skel-bar-lg" />
      <div className="abr-skel-bar abr-skel-bar-md" />
      <div className="abr-skel-grid">
        <div className="abr-skel-block" />
        <div className="abr-skel-block" />
        <div className="abr-skel-block" />
      </div>
      <div className="abr-skel-bar abr-skel-bar-sm" />
      <span className="abr-sr-only">Loading active buyer report</span>
    </div>
  </div>
);

const NotFoundState = ({ inventoryId, isShared }) => (
  <div className="abr-page-state" role="alert">
    <div className="abr-page-state-inner">
      <h1>Report not available</h1>
      <p>
        {isShared
          ? "This shared report is no longer available, or the link has expired."
          : "We couldn't find an active listing for this report. It may have been removed, or you may not have permission to view it."}
      </p>
      {!isShared && (
        <a className="abr-page-state-link" href="/my-inventory">Back to My Inventory</a>
      )}
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="abr-page-state" role="alert">
    <div className="abr-page-state-inner">
      <h1>Something went wrong</h1>
      <p>{message || "We hit a snag loading this report. Please try again."}</p>
      <button type="button" className="abr-page-state-btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  </div>
);

const ActiveBuyerReport = () => {
  // /reports/active-buyer/:inventoryId  (agent) | /r/:token  (public share)
  const { inventoryId, token } = useParams();
  const isShared = Boolean(token);
  const reportKey = isShared ? token : inventoryId;
  const [searchParams] = useSearchParams();
  const editMode = !isShared && searchParams.get("edit") === "1";
  const mode = editMode ? "agent" : "seller";

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!reportKey) {
      setError({ status: 404, message: "Missing report id" });
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReportData(reportKey, { signal: controller.signal, shared: isShared })
      .then((data) => {
        if (cancelled) return;
        setReportData(adaptApiResponse(data));
      })
      .catch((err) => {
        if (cancelled || err?.name === "AbortError") return;
        setError({ status: err?.status || 0, message: err?.message || "Failed to load report" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [reportKey, isShared, attempt]);

  if (loading) return <LoadingSkeleton />;
  if (error?.status === 404) return <NotFoundState inventoryId={reportKey} isShared={isShared} />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!reportData) return <ErrorState message="No report data returned" onRetry={refetch} />;

  return (
    <ActiveBuyerReportView
      reportData={reportData}
      mode={mode}
      inventoryId={inventoryId}
      isShared={isShared}
      onDownloadPdf={isShared ? () => downloadSharedReportPdf(token) : downloadReportPdf}
    />
  );
};

export default ActiveBuyerReport;
